/**
 * driverBridge — small helper to reuse driver.js for highlighting elements
 * the AI agent points to (e.g. response contains a selector → flash it).
 *
 * driver.js is already in dependencies (used by WorkflowTourProvider).
 */
import { driver, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

let activeDriver: Driver | null = null;

/**
 * Briefly highlight an element with driver.js.
 * Auto-dismisses after `durationMs`.
 */
export function highlightSelector(
  selector: string,
  options: { title?: string; description?: string; durationMs?: number } = {},
): boolean {
  const el = document.querySelector(selector);
  if (!el) return false;

  // Tear down any prior agent highlight
  try {
    activeDriver?.destroy();
  } catch {
    /* ignore */
  }

  const d = driver({
    showProgress: false,
    animate: true,
    allowClose: true,
    overlayColor: 'rgba(0, 0, 0, 0.35)',
    stagePadding: 8,
    stageRadius: 10,
    popoverClass: 'tour-popover',
  });

  d.highlight({
    element: selector,
    popover: {
      title: options.title || '💡 Trợ lý chỉ dẫn',
      description: options.description || 'Đây là vị trí bạn cần.',
      side: 'bottom',
      align: 'center',
      showButtons: ['close'] as any,
    },
  });

  activeDriver = d;

  const ms = options.durationMs ?? 6000;
  if (ms > 0) {
    window.setTimeout(() => {
      try {
        d.destroy();
      } catch {
        /* ignore */
      }
      if (activeDriver === d) activeDriver = null;
    }, ms);
  }

  return true;
}

/** Extract the first [data-tour="..."] selector from arbitrary text. */
export function extractTourSelector(text: string): string | null {
  const match = text.match(/\[data-tour=["']([\w-]+)["']\]/);
  return match ? `[data-tour="${match[1]}"]` : null;
}
